import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../utils/api';

/**
 * Searchable client dropdown.
 * Props:
 *   value       – current client_id (string or number, '' when empty)
 *   initialLabel – display name when value is pre-set (e.g. "KUA-C001 — Ramesh")
 *   service     – optional service filter, e.g. "IT Returns" or "ESI,PF"
 *   onChange    – (id: string, displayName: string) => void
 *   required    – bool
 *   placeholder – string
 */
export default function ClientSearchDropdown({
  value,
  initialLabel = '',
  service,
  onChange,
  required = false,
  placeholder = 'Type 2+ chars to search clients…',
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [focusedIdx, setFocusedIdx] = useState(-1);
  // Display label for the currently selected client
  const [selectedLabel, setSelectedLabel] = useState(initialLabel);

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync external label changes (when editing form opens with a pre-selected client)
  useEffect(() => {
    setSelectedLabel(initialLabel || '');
  }, [initialLabel]);

  // Reset when value is cleared externally
  useEffect(() => {
    if (!value) { setSelectedLabel(''); setQuery(''); setResults([]); setOpen(false); }
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setFocusedIdx(-1);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const doSearch = useCallback((q) => {
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { search: q };
        if (service) params.service = service;
        const res = await api.get('/clients', { params });
        const list = (res.data || []).slice(0, 10);
        setResults(list);
        setOpen(list.length > 0 || q.length >= 2);
        setFocusedIdx(-1);
      } catch {}
      setLoading(false);
    }, 220);
  }, [service]);

  const handleInput = (e) => {
    const q = e.target.value;
    setQuery(q);
    doSearch(q);
  };

  const formatLabel = (c) =>
    c.client_code ? `${c.client_code} — ${c.name}` : c.name;

  const select = (client) => {
    const label = formatLabel(client);
    setSelectedLabel(label);
    setQuery('');
    setResults([]);
    setOpen(false);
    setFocusedIdx(-1);
    onChange(String(client.id), label);
  };

  const clear = (e) => {
    e.stopPropagation();
    setSelectedLabel('');
    setQuery('');
    setResults([]);
    setOpen(false);
    onChange('', '');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (focusedIdx >= 0 && results[focusedIdx]) select(results[focusedIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setFocusedIdx(-1);
    }
  };

  const isSelected = value && selectedLabel;

  return (
    <div ref={wrapperRef} className="relative">
      {isSelected ? (
        /* Selected state — show name chip with clear button */
        <div className="input-field flex items-center gap-2 cursor-default min-h-[38px]">
          <span className="flex-1 text-sm text-gray-800 truncate">{selectedLabel}</span>
          <button
            type="button"
            onClick={clear}
            className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0 text-lg leading-none"
            title="Clear selection"
          >
            &times;
          </button>
        </div>
      ) : (
        /* Search input */
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            className="input-field pr-8 w-full"
            placeholder={placeholder}
            value={query}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            onFocus={() => { if (results.length > 0) setOpen(true); }}
            required={required && !value}
            autoComplete="off"
          />
          {loading && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs animate-pulse">
              ⟳
            </span>
          )}
        </div>
      )}

      {/* Results dropdown */}
      {open && (
        <div className="absolute z-50 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 overflow-hidden">
          {results.length > 0 ? (
            <ul className="max-h-48 overflow-y-auto">
              {results.map((c, idx) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => select(c)}
                    className={`w-full text-left px-3 py-2 text-sm border-b border-gray-50 last:border-0 transition-colors ${
                      idx === focusedIdx ? 'bg-blue-50 text-blue-800' : 'hover:bg-gray-50'
                    }`}
                  >
                    {c.client_code && (
                      <span className="font-mono text-xs font-semibold text-[#1e3a5f] mr-1">{c.client_code}</span>
                    )}
                    {c.client_code && <span className="text-gray-400 mr-1">—</span>}
                    <span className="font-medium">{c.name}</span>
                    {c.pan && <span className="text-gray-400 text-xs ml-2">{c.pan}</span>}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            !loading && query.length >= 2 && (
              <div className="px-3 py-2 text-sm text-gray-400 italic">No clients found</div>
            )
          )}
        </div>
      )}
    </div>
  );
}
